import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";

import { BankLogo } from "@dfp/react-components";

import { makeScaInstructionNumber } from "../demoBankConnect.utils.ts";
import type { BankFlowProps, BankFlowStep } from "./bankFlow.types.ts";

// Replica of Fio banka's PSD2 pages (see Confluence "Screen flow when
// connecting to a bank" → FIO). The copy is intentionally hard-coded Czech —
// these are external bank pages, not localized app UI.
const FIO_NAVY = "#1B4F7E";
const FIO_WORDMARK = "#1F4E9E";
const FIO_GREEN = "#9DC226";
const FIO_LINK = "#2456A0";
const FIO_TEXT = "#333333";

const FONT_STACK = '"Helvetica Neue", Helvetica, Arial, sans-serif';

function FioHeader({ showLanguage }: { showLanguage: boolean }) {
  return (
    <Stack
      direction="row"
      sx={{ alignItems: "center", justifyContent: "space-between", py: 2 }}
    >
      <Stack direction="row" sx={{ alignItems: "center", gap: 1.5 }}>
        <BankLogo
          bank="FIO"
          style={{ height: 40, width: "auto", display: "block" }}
        />
        <Typography
          sx={{
            color: FIO_WORDMARK,
            fontWeight: 700,
            fontSize: 30,
            letterSpacing: "0.02em",
            fontFamily: FONT_STACK,
          }}
        >
          Fio banka
        </Typography>
      </Stack>
      {showLanguage && (
        <Button
          variant="outlined"
          size="small"
          sx={{
            color: FIO_TEXT,
            borderColor: "#c9c9c9",
            bgcolor: "#fff",
            borderRadius: 0.5,
            fontFamily: FONT_STACK,
            fontSize: 13,
            "&:hover": { bgcolor: "#f5f5f5", borderColor: "#c9c9c9" },
          }}
        >
          🇨🇿 Česky ▾
        </Button>
      )}
    </Stack>
  );
}

function FioSectionHeading({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ borderBottom: `2px solid ${FIO_GREEN}`, pb: 0.75, mb: 2 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 15,
          color: FIO_TEXT,
          fontFamily: FONT_STACK,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function FioSecurityColumn() {
  return (
    <Box>
      <FioSectionHeading>Informace o bezpečnosti</FioSectionHeading>
      <Typography
        sx={{ fontSize: 13.5, color: FIO_TEXT, fontFamily: FONT_STACK, mb: 2 }}
      >
        <strong>Víte, že…</strong> Fio banka po svých klientech nikdy
        nepožaduje, aby svoje přihlašovací údaje k elektronickým aplikacím
        zadávali na jiném místě než k tomu určeném přihlašovacím formuláři na
        domovských stránkách banky www.fio.cz, a v žádném případě nevybízí k
        takovému úkonu prostřednictvím odkazu obsaženého v e-mailové zprávě?
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: FIO_TEXT, fontFamily: FONT_STACK }}>
        Pokud jste se na tuto stránku dostali jinak než z webové stránky
        www.fio.cz, nebo proklikem ze svých oblíbených položek v prohlížeči,
        ujistěte se, prosím, že jste skutečně na stránkách Fio banky. Pod ikonou
        zámečku byste vždy měli najít certifikát dokládající, že se skutečně
        nacházíte na stránkách Fio banky. Více o bezpečném používání internetu
        naleznete <Link sx={{ color: FIO_LINK }}>zde</Link>.
      </Typography>
    </Box>
  );
}

function FioFooter() {
  return (
    <Box sx={{ borderTop: "1px solid #e0e0e0", mt: 8, py: 2 }}>
      <Typography
        sx={{
          textAlign: "right",
          fontSize: 13,
          color: FIO_TEXT,
          fontFamily: FONT_STACK,
        }}
      >
        Copyright © 2026 Fio banka {"| "}
        <Link sx={{ color: FIO_LINK }}>Právní prohlášení</Link>
        {" | "}
        <Link sx={{ color: FIO_LINK }}>RM-SYSTÉM</Link>
        {" | "}
        <Link sx={{ color: FIO_LINK }}>AKCIE.CZ</Link>
      </Typography>
    </Box>
  );
}

const fioInputSx = {
  bgcolor: "#fff",
  borderRadius: 0.5,
  width: 260,
  "& .MuiOutlinedInput-root": { borderRadius: 0.5 },
  "& input": { py: 0.9, px: 1.25, fontSize: 14, fontFamily: FONT_STACK },
};

/** Fio banka PSD2 flow: login → SCA push authorization → account consent. */
export function FioFlow({
  thirdPartyName,
  ownerName,
  username,
  connectionId,
  accounts,
  initialStep,
  onConnect,
  onCancel,
}: BankFlowProps) {
  const [step, setStep] = useState<BankFlowStep>(initialStep);
  const [loginName, setLoginName] = useState(username);
  const [password, setPassword] = useState("fio-demo-password");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(accounts.map((account) => account.id)),
  );

  // SCA push approval "arrives" by itself after a moment, like the real
  // mobile-app confirmation would.
  useEffect(() => {
    if (step !== "sca") return;
    const timer = setTimeout(() => setStep("accounts"), 3000);
    return () => clearTimeout(timer);
  }, [step]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const greenButtonSx = {
    bgcolor: FIO_GREEN,
    color: "#fff",
    borderRadius: 0.5,
    fontFamily: FONT_STACK,
    fontWeight: 700,
    px: 2.5,
    boxShadow: "none",
    "&:hover": { bgcolor: "#8BAD1E", boxShadow: "none" },
  };

  return (
    <Box
      sx={{ minHeight: "100vh", bgcolor: "#fff", fontFamily: FONT_STACK }}
    >
      <Container maxWidth="lg">
        <FioHeader showLanguage={step !== "sca"} />

        {step === "login" && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" },
              gap: 6,
              mt: 2,
            }}
          >
            <Box>
              <FioSectionHeading>
                Povolení přístupu k účtu/účtům pro{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  &apos;{thirdPartyName}&apos;
                </Box>
              </FioSectionHeading>

              <Box
                sx={{ bgcolor: FIO_NAVY, borderRadius: 1, p: 3.5, mt: 2.5 }}
              >
                <Stack sx={{ gap: 1.75 }}>
                  <Stack
                    direction="row"
                    sx={{ alignItems: "center", gap: 2 }}
                  >
                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: 14,
                        fontWeight: 700,
                        width: 150,
                        textAlign: "right",
                        fontFamily: FONT_STACK,
                      }}
                    >
                      Uživatelské jméno
                    </Typography>
                    <TextField
                      value={loginName}
                      onChange={(event) => setLoginName(event.target.value)}
                      sx={fioInputSx}
                    />
                    <Typography sx={{ color: "#fff", fontSize: 18 }}>⌨</Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    sx={{ alignItems: "center", gap: 2 }}
                  >
                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: 14,
                        fontWeight: 700,
                        width: 150,
                        textAlign: "right",
                        fontFamily: FONT_STACK,
                      }}
                    >
                      Heslo
                    </Typography>
                    <TextField
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      sx={fioInputSx}
                    />
                    <Typography sx={{ color: "#fff", fontSize: 18 }}>⌨</Typography>
                  </Stack>

                  <Box sx={{ pl: "166px", pt: 0.5 }}>
                    <Button
                      variant="contained"
                      onClick={() => setStep("sca")}
                      disabled={!loginName || !password}
                      sx={greenButtonSx}
                    >
                      Přihlásit se
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </Box>
            <FioSecurityColumn />
          </Box>
        )}

        {step === "sca" && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" },
              gap: 6,
              mt: 2,
            }}
          >
            <Box>
              <FioSectionHeading>SCA přihlášení do aplikace</FioSectionHeading>

              <Box
                sx={{
                  bgcolor: "#FBF7E4",
                  border: "1px solid #EAE3C5",
                  borderRadius: 1,
                  p: 2.5,
                  mt: 2.5,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: FIO_TEXT,
                    fontFamily: FONT_STACK,
                    mb: 1.5,
                  }}
                >
                  🔒 Autorizace
                </Typography>
                <CircularProgress size={16} sx={{ color: "#8a8a8a", mb: 1.5 }} />
                <Typography
                  sx={{ fontSize: 14, color: FIO_TEXT, fontFamily: FONT_STACK }}
                >
                  Autorizujte prosím pokyn{" "}
                  <strong>č. {makeScaInstructionNumber(connectionId)}</strong> v
                  mobilní aplikaci přes zaslanou push notifikaci.
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: FIO_TEXT,
                    fontFamily: FONT_STACK,
                    mt: 1,
                  }}
                >
                  Nepřišla Vám push notifikace?{" "}
                  <Link sx={{ color: FIO_LINK, cursor: "pointer" }}>
                    zaslat znovu
                  </Link>
                </Typography>

                <Divider sx={{ my: 1.75, borderColor: "#EAE3C5" }} />

                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: FIO_TEXT,
                    fontFamily: FONT_STACK,
                    mb: 0.75,
                  }}
                >
                  Alternativní způsob autorizace
                </Typography>
                <Typography sx={{ fontSize: 14, fontFamily: FONT_STACK }}>
                  <Link
                    sx={{ color: FIO_LINK, cursor: "pointer" }}
                    onClick={() => setStep("accounts")}
                  >
                    SMS autorizace
                  </Link>
                  {"  |  "}
                  <Link
                    sx={{ color: FIO_LINK, cursor: "pointer" }}
                    onClick={() => setStep("accounts")}
                  >
                    Offline autorizace
                  </Link>
                </Typography>
              </Box>
            </Box>
            <FioSecurityColumn />
          </Box>
        )}

        {step === "accounts" && (
          <Box sx={{ mt: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 24,
                color: FIO_TEXT,
                fontFamily: FONT_STACK,
                borderBottom: "1px solid #d9d9d9",
                pb: 1.25,
                mb: 2.5,
              }}
            >
              Povolení přístupu k účtům třetí straně
            </Typography>

            <Typography
              sx={{
                color: FIO_WORDMARK,
                fontWeight: 700,
                fontSize: 13.5,
                fontFamily: FONT_STACK,
                mb: 0.75,
              }}
            >
              Třetí strana žádající o přístup
            </Typography>
            <Typography
              sx={{ fontSize: 14, color: FIO_TEXT, fontFamily: FONT_STACK }}
            >
              {thirdPartyName}
            </Typography>

            <Divider sx={{ my: 2.5 }} />

            <Typography
              sx={{
                color: FIO_WORDMARK,
                fontWeight: 700,
                fontSize: 13.5,
                fontFamily: FONT_STACK,
                mb: 1.25,
              }}
            >
              Účty, ke kterým povolím přístup
            </Typography>
            <Box
              data-tour="bank-accounts"
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                columnGap: 6,
                rowGap: 1,
                maxWidth: 900,
              }}
            >
              {accounts.map((account) => (
                <Stack
                  key={account.id}
                  direction="row"
                  onClick={() => toggle(account.id)}
                  sx={{ alignItems: "center", gap: 1, cursor: "pointer" }}
                >
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(account.id)}
                    sx={{
                      p: 0,
                      color: "#1976d2",
                      "&.Mui-checked": { color: "#1976d2" },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: FIO_TEXT,
                      fontFamily: FONT_STACK,
                    }}
                  >
                    {ownerName} ({account.accountNumber.split("/")[0]} -{" "}
                    {account.currency})
                  </Typography>
                </Stack>
              ))}
            </Box>

            <Divider sx={{ my: 2.5 }} />

            <Typography
              sx={{
                color: FIO_WORDMARK,
                fontWeight: 700,
                fontSize: 13.5,
                fontFamily: FONT_STACK,
                mb: 0.75,
              }}
            >
              Služby, které poskytnu třetí straně
            </Typography>
            <Typography
              sx={{ fontSize: 14, color: FIO_TEXT, fontFamily: FONT_STACK }}
            >
              <strong>Pasivní operace</strong> - tato služba umožňuje pouze
              zobrazení zůstatků, pohybů na účtech, plánované platby a další
              přehledy.
            </Typography>
            <Typography
              sx={{ fontSize: 14, color: FIO_TEXT, fontFamily: FONT_STACK }}
            >
              <strong>Aktivní operace</strong> - tato služba umožňuje zadávat
              příkazy k úhradě, případně je rušit.
            </Typography>

            <Typography
              sx={{
                color: FIO_WORDMARK,
                fontWeight: 700,
                fontSize: 14,
                fontFamily: FONT_STACK,
                mt: 2.5,
              }}
            >
              Po stisknutí tlačítka &quot;Odeslat&quot; dojde k povolení
              přístupu k vybraným účtům prostřednictvím uvedených služeb pro
              třetí stranu a vrátíte se na stránky třetí strany.
            </Typography>

            <Stack direction="row" sx={{ gap: 1, mt: 3 }}>
              <Button
                data-tour="bank-connect-submit"
                variant="contained"
                disabled={selectedIds.size === 0}
                onClick={() =>
                  onConnect(
                    accounts.filter((account) =>
                      selectedIds.has(account.id),
                    ),
                  )
                }
                sx={greenButtonSx}
              >
                Odeslat
              </Button>
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{
                  color: FIO_TEXT,
                  borderColor: "#c9c9c9",
                  borderRadius: 0.5,
                  fontFamily: FONT_STACK,
                  "&:hover": { bgcolor: "#f5f5f5", borderColor: "#c9c9c9" },
                }}
              >
                Zpět
              </Button>
            </Stack>

            <Typography
              sx={{
                fontSize: 12.5,
                color: FIO_TEXT,
                fontFamily: FONT_STACK,
                mt: 2,
              }}
            >
              * povinná položka
            </Typography>
          </Box>
        )}

        <FioFooter />
      </Container>
    </Box>
  );
}
